import { useState } from "react"
import PubSub from "pubsub-js"
import Typography from "aldoo-ra/Typography"
import TextBundle from "aldoo-ra/CMS/text-bundle"
import Button from "aldoo-ra/Button"
import Modal from "aldoo-ra/Modal"
import { CheckCircleIcon, ArrowPathIcon } from "@heroicons/react/24/outline"
import PlatformAuth from "aldoo-ra/PlatformAuth"
import AldooStoreAPI from "aldoo-ra/Store/api"

const { useAuth } = PlatformAuth

export default function PaymentSuccess({ store, children }) {
  const texts = TextBundle({ id: "aldoo-store" })
  const [auth, setAuth] = useAuth()
  const [showValidationModal, setShowValidationModal] = useState(false)
  const [validationStatus, setValidationStatus] = useState('checking') // 'checking', 'validated', 'error'
  const [validationError, setValidationError] = useState(null)

  //Store interface
  const { paymentReceipt, setPaymentSuccessVisible } = store
  const {
    paymentInfo,
    paymentIntentId,
    subscriptionId,
    requiresVerification,
    existingUserEmail,
    existingUserCredentials
  } = paymentReceipt || {}

  const handleGoToAccount = async () => {
    // EXISTING USER: Not logged in, needs email verification first
    if (requiresVerification && existingUserCredentials) {
      setValidationStatus('awaiting-verification')
      setShowValidationModal(true)
      await initiateLoginVerification()
      return
    }

    // For free purchases - redirect immediately (no processing needed)
    const isFreeType = paymentInfo?.type === "free_purchase" || paymentInfo?.type === "free_subscription"

    if (isFreeType) {
      window.location.href = "/account/licenses"
      return
    }

    // If we don't have a paymentIntentId or subscriptionId, redirect immediately
    // (this shouldn't happen for paid purchases, but handle gracefully)
    if (!paymentIntentId && !subscriptionId) {
      window.location.href = "/account/licenses"
      return
    }

    // Show validation modal BEFORE starting poll
    setShowValidationModal(true)
    setValidationStatus('checking')
    setValidationError(null)

    // Start polling after a small delay to ensure modal is rendered
    setTimeout(() => {
      pollForValidation()
    }, 100)
  }

  const initiateLoginVerification = () => {
    // Close the validation modal
    setShowValidationModal(false)

    // If they're already logged in (auth exists), just redirect
    if (auth) {
      window.location.href = "/account/licenses"
      return
    }

    // Close the payment success modal since signin dialog will open
    setPaymentSuccessVisible(false)

    // Trigger signin with pre-filled email
    // The device was already logged out by prepare-purchase-auth
    // So the signin flow will send magic link for verification
    PubSub.publish("signin", {
      redirect: "/account",
      email: existingUserEmail,
      provider: "email"
    })
  }

  const pollForValidation = async () => {
    let attempts = 0
    const maxAttempts = 60 // 60 seconds max

    const checkValidation = async () => {
      try {
        attempts++
        const result = await AldooStoreAPI.checkPaymentProcessing(paymentIntentId, subscriptionId)

        if (result.processed) {
          setValidationStatus('validated')
          // Refresh profile to get updated account status
          await PlatformAuth.refreshProfile()
          // Redirect to account after a brief delay
          setTimeout(() => {
            window.location.href = "/account/licenses"
          }, 500)
          return true // Stop polling
        } else {
          if (attempts >= maxAttempts) {
            setValidationStatus('error')
            setValidationError('Purchase validation timed out. Your purchase was successful, but processing is taking longer than expected. Please check your account in a few moments.')
            return true // Stop polling
          }
          return false // Continue polling
        }
      } catch (error) {
        if (attempts >= maxAttempts) {
          setValidationStatus('error')
          setValidationError('Unable to validate purchase. Your payment was successful. Please check your account in a few moments.')
          return true // Stop polling
        }
        return false // Continue polling
      }
    }

    // Poll immediately, then every 1 second
    const shouldStop = await checkValidation()
    if (shouldStop) return

    const pollInterval = setInterval(async () => {
      const shouldStop = await checkValidation()
      if (shouldStop) {
        clearInterval(pollInterval)
      }
    }, 1000)
  }

  const handleCloseModal = () => {
    setShowValidationModal(false)
  }

  const handleRetryValidation = () => {
    setValidationStatus('checking')
    setValidationError(null)
    pollForValidation()
  }

  const getSuccessMessage = () => {
    //Free
    if (paymentInfo.type === "free_subscription") {
      return (
        texts?.free_subscription_success ||
        "Your free subscription has been activated successfully!"
      )
    }

    if (paymentInfo.type === "free_purchase") {
      return (
        texts?.free_purchase_success ||
        "Your free purchase has been activated successfully!"
      )
    }

    //Trial
    if (paymentInfo.hasTrial) {
      return texts?.trial_success || "Your trial has been activated."
    }

    //Subscription
    if (paymentInfo.type === "subscription") {
      return (
        texts?.subscription_success ||
        "Your subscription has been activated successfully!"
      )
    }

    //Direct payment
    if (paymentInfo.type === "purchase") {
      return (
        texts?.payment_success || "Your payment was processed successfully!"
      )
    }
  }

  return (
    <>
      {/* Only show success content if validation modal is not active */}
      {!showValidationModal && (
        <div className="p-6 flex flex-col items-center text-center">
          <CheckCircleIcon className="w-16 h-16 text-primary mb-4" />

          <Typography
            component="h1"
            variant="h2"
            className="text-text mb-4"
          >
            {texts?.thank_you || "Thank You!"}
          </Typography>

          <Typography
            variant="txt"
            className="block text-text mb-6"
          >
            {getSuccessMessage()}
          </Typography>

          {/* Show "Go To My Account" button for logged in users OR users who need to verify email */}
          {(auth || requiresVerification) && (
            <div className="w-full flex justify-center mb-6">
              <Button
                variant="primary"
                onClick={handleGoToAccount}
              >
                <Typography
                  variant="body1_accent"
                  className="text-text_inv"
                >
                  {texts?.view_your_licenses || "View your licenses"}
                </Typography>
              </Button>
            </div>
          )}

          {/* Features section at bottom with lower accent */}
          {children && (
            <div className="w-full border-t border-bordered pt-6 mt-2">
              {children}
            </div>
          )}
        </div>
      )}

      {/* Validation content - shown in place of success content */}
      {showValidationModal && (
        <div className="p-6 flex flex-col items-center text-center min-w-[300px]">
          {validationStatus === 'checking' && (
            <>
              <ArrowPathIcon className="w-12 h-12 text-primary mb-4 animate-spin" />

              <Typography
                variant="h3"
                className="mb-2"
              >
                Accessing Account...
              </Typography>

              <Typography variant="txt1_dim">
                Please wait while we verify your account
              </Typography>
            </>
          )}

          {validationStatus === 'awaiting-verification' && (
            <>
              <CheckCircleIcon className="w-12 h-12 text-primary mb-4" />
              <Typography
                variant="h3"
                className="mb-2"
              >
                Please Verify Your Email
              </Typography>

              <Typography
                variant="txt1"
                className="block text-text text-center mb-2"
              >
                We've sent a verification link to <strong>{existingUserEmail}</strong>
              </Typography>

              <Typography
                variant="txt1_dim"
                className="text-center"
              >
                Click the link in your email to sign in and access your account
              </Typography>
            </>
          )}

          {validationStatus === 'validated' && (
            <>
              <CheckCircleIcon className="w-12 h-12 text-success mb-4" />
              <Typography
                variant="h3"
                className="mb-2"
              >
                Purchase Validated
              </Typography>

              <Typography variant="txt1_dim">
                Redirecting to your account...
              </Typography>
            </>
          )}

          {validationStatus === 'error' && (
            <>
              <Typography
                variant="h3"
                className="mb-4"
              >
                Validation Delayed
              </Typography>

              <Typography
                variant="txt1"
                className="block text-text mb-4"
              >
                {validationError}
              </Typography>

              <div className="flex gap-2">
                <Button variant="secondary" onClick={handleCloseModal}>
                  Close
                </Button>

                <Button variant="primary" onClick={handleRetryValidation}>
                  Retry
                </Button>
              </div>
            </>
          )}
        </div>
      )}
    </>
  )
}
